import { useState, useCallback } from 'react';
import { Box, Typography } from '@mui/material';
import { sortBy } from 'lodash-es';
import { EmptySidePanel } from '../../../components/side-panel/EmptySidePanel.jsx';
import { SidePanel } from '../../../components/side-panel/SidePanel.jsx';
import { CollapsibleSection } from '../../../components/side-panel/CollapsibleSection.jsx';
import LocalDate from '../../../components/LocalDate.jsx';
import { useFields } from '../../../hooks/use-fields.js';
import { InstanceInputs } from './InstanceInputs.jsx';

export function InstanceSidePanel(props) {
  const { close, instance } = props;
  const { focusAaid, focusedAaid } = useFields();
  const [expandedIds, setExpandedIds] = useState([]);

  const handleAccordionChange = useCallback(
    (id) => (event, isExpanded) => {
      setExpandedIds((prev) =>
        isExpanded ? [...prev, id] : prev.filter((x) => x !== id),
      );
    },
    [],
  );

  return (
    <SidePanel title="Instance Details" subtitle={instance?.name} close={close}>
      {buildBody()}
    </SidePanel>
  );

  function buildBody() {
    if (!instance) return <EmptySidePanel />;

    return (
      <>
        <CollapsibleSection
          key={instance.id}
          expanded={expandedIds.includes(instance.id)}
          onChange={handleAccordionChange(instance.id)}
          title="Input Data"
        >
          <InstanceInputs instance={instance} />
        </CollapsibleSection>

        {sortBy(instance.fields, 'name').map((f) => buildFieldDetails(f))}
      </>
    );

    function buildFieldDetails(rxField) {
      return (
        <Box
          key={rxField.aaid}
          data-testid={`field-details-${rxField.aaid}`}
          className={rxField.aaid === focusedAaid ? 'focused' : ''}
          sx={{
            cursor: 'pointer',
            border: '1px solid #e0e0e0',
            borderRadius: 2,
            pt: 1.5,
            pb: 1,
            px: 2,
            mb: 1,
          }}
          onClick={() =>
            focusedAaid === rxField.aaid
              ? focusAaid(null)
              : focusAaid(rxField.aaid)
          }
        >
          <Typography sx={{ fontWeight: 500, mb: 2 }}>
            {rxField.name}
          </Typography>
          {buildInstanceInfo(
            'Yield Referenced',
            getYieldValues(rxField)?.join(' / '),
          )}
          {buildInstanceInfo('Tillage Type', rxField.tillageType)}
          {buildInstanceInfo(
            'Reference Soil Date',
            getReferenceSoilDate(rxField),
          )}
          {buildInstanceInfo(
            'L Min / Max / Avg',
            getFieldStats('lime', rxField),
          )}
          {buildInstanceInfo(
            'P Min / Max / Avg',
            getFieldStats('phosphorus', rxField),
          )}
          {buildInstanceInfo(
            'K Min / Max / Avg',
            getFieldStats('potassium', rxField),
          )}
        </Box>
      );
    }
  }

  function buildInstanceInfo(title, value) {
    return value || value === 0 ? (
      <Box display="flex" justifyContent="space-between" mb={0.5}>
        <span>{title}</span>
        <span>{value}</span>
      </Box>
    ) : null;
  }

  function getYieldValues(rxField) {
    return ['phosphorus', 'potassium']
      .map((name) => [rxField[name]?.yield1Value, rxField[name]?.yield2Value])
      .map((values) => values.filter(Boolean))
      .find((values) => values.length);
  }

  function getReferenceSoilDate(rxField) {
    const soilDate = ['lime', 'phosphorus', 'potassium']
      .map((name) => rxField[name]?.soilDate)
      .filter(Boolean)
      .at(0);

    return soilDate && <LocalDate value={soilDate} timeZone="UTC" />;
  }

  function getFieldStats(nutrient, rxField) {
    const rx = rxField[nutrient];
    const stats = rx?.stats;
    if (rx?.statusDetails) {
      return (
        <Box sx={{ color: (theme) => theme.palette.error.main }}>
          {rx.statusDetails}
        </Box>
      );
    }
    if (!stats?.avg) return;

    return `${stats.min} / ${stats.max} / ${Math.round(stats.avg)}`;
  }
}
